import {Grid, Paper} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useQuery} from '@tanstack/react-query';
import {format} from 'date-fns';
import React, {useState} from 'react';

import CustomSnackbar from '../../components/CustomSnackBar/CustomSnackBar';
import CustomTable from '../../components/CustomTable/CustomTable';
import ToolBar from '../../components/ToolBar/ToolBar';
import {fetchAdminOperationHistory} from '../../service/admin';

import AdminDrawer from './AdminDrawer';
import ConfigMongo from './TaskComponents/ConfigMongo';
import DataGridTasks from './TaskComponents/DataGridTask';
import Logging from './TaskComponents/Logging';
import RedisOperation from './TaskComponents/RedisOperation';
import RefreshConfigSQL from './TaskComponents/RefreshConfigSQL';

const StyledPaper = styled(Paper)(({theme}) => ({
  borderRadius: '20px',
  height: 100,
  width: 100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  color: '#628bf5',
  backgroundColor: '#ffff',
  border: '2px solid #628bf5',
  fontWeight: 'bold',
  padding: '10px 10px 10px 10px',
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: '#eaf1ff',
    border: '2px solid #628bf5',
  },
}));

const Admin = () => {
  const [snackbarAlert, setSnackBarAlert] = useState({isOpen: false, message: '', severity: 'warning'});
  const [selectedTask, setSelectedTask] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);

  const tasks = [
    {
      id: 'partner',
      taskName: 'Partners',
      component: DataGridTasks,
    },
    {
      id: 'channel',
      taskName: 'Channels',
      component: DataGridTasks,
    },
    {
      id: 'channelStatusMapping',
      taskName: 'Channel Status Mapping',
      component: DataGridTasks,
    },
    {
      id: 'channelRejectionMsgMapping',
      taskName: 'Channel Rejection Message Mapping',
      component: DataGridTasks,
    },
    {
      id: 'configSQL',
      taskName: 'Config SQL',
      defaultTask: 'editConfigSQL',
      isMultiComponent: true,
      childTasks: [
        {
          id: 'editConfigSQL',
          taskName: 'Get/Edit Config',
          component: DataGridTasks,
        },
        {
          id: 'refreshConfigSQL',
          taskName: 'Refresh Config',
          component: RefreshConfigSQL,
        },
      ],
    },
    {
      id: 'configMONGO',
      taskName: 'Config MONGO',
      component: ConfigMongo,
    },
    {
      id: 'flushQueues',
      taskName: 'Flush Queues',
      component: DataGridTasks,
    },
    {
      id: 'redis',
      taskName: 'Redis Operations',
      component: RedisOperation,
    },
    {
      id: 'updateLogging',
      taskName: 'Logging',
      component: Logging,
    },
    {
      id: 'cron',
      taskName: 'Cron',
      component: DataGridTasks,
    },
  ];

  const handleDrawerOpen = (task) => {
    setSelectedTask(task);
    setOpenDrawer(true);
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
    setSelectedTask(null);
  };

  const closeSnackBarAlert = () => {
    setSnackBarAlert((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  };

  const columns = [
    {
      id: 'Date',
      header: 'Date',
      filterable: false,
      accessorFn: (row) =>
        `${format(new Date(row.timestamp), 'dd MMM')} ${format(new Date(row.timestamp), 'HH:mm:ss')}`,
    },
    {
      id: 'Rebel Correlation Id',
      header: 'Rebel Correlation Id',
      accessorKey: 'rebelCorrelationId',
      filterable: false,
    },
    {
      id: 'User',
      header: 'User',
      accessorKey: 'user',
      filterable: false,
    },
    {
      id: 'Message',
      header: 'Message',
      accessorKey: 'message',
      accessorFn: (row) => `[${row?.actionType}] ${row?.message}`,
      filterable: false,
    },
  ];

  const [globalFilter, setGlobalFilter] = useState('');

  const dataQuery = useQuery({
    queryKey: ['data-admin-history'],
    queryFn: () => fetchAdminOperationHistory(),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return (
    <>
      <ToolBar headerText={'Admin'} />
      <Grid container justifyContent="center" spacing={2}>
        {tasks.map((task, i) => (
          <Grid key={i} item>
            <StyledPaper
              onClick={() => {
                handleDrawerOpen(task);
              }}
              elevation={24}
            >
              {' '}
              {task.taskName}
            </StyledPaper>
          </Grid>
        ))}
      </Grid>
      <CustomSnackbar
        autoHideDuration={5000}
        message={snackbarAlert.message}
        isOpen={snackbarAlert.isOpen}
        showCloseButton={true}
        handleClose={closeSnackBarAlert}
        isAlert={true}
        severity={snackbarAlert?.severity}
        anchorOriginVertical={'bottom'}
      />
      {selectedTask && (
        <AdminDrawer
          open={openDrawer}
          onToggleOff={handleDrawerClose}
          selectedTask={selectedTask}
          setSnackBarAlert={setSnackBarAlert}
        />
      )}
      <br />
      <CustomTable
        tableKey={'admin-history'}
        tableHeight={68}
        columns={columns}
        dataQuery={dataQuery}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        enablePagination={false}
        allowDateRangeSelection={false}
      />
    </>
  );
};

export default Admin;
